import AssessmentIcon from '@mui/icons-material/Assessment';
import { Chip, Divider, Stack, Typography, useTheme } from '@mui/material';
import { GenericFallbacksWrapper } from '../../../../components/Fallback/GenericFallbacksWrapper';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { FMT } from '../../../../util/formatter-service';
import { chipStack, FinanceDateSelector, PeriodPicker, PeriodRangePicker } from './FinanceDatePickers';
import { useFinancialsDateSettings, useMostRecentReportDate } from './useFinancialsDateSettings';

export function FinancialsDateSettings2() {
  const {
    label,
    selectedFrequency,
    pastRange,
    futureRange,
    isMaxMode,
    handleSelectRange,
    handleFrequencyChange,
    handleMaxToggle,
    auditorConstraints,
  } = useFinancialsDateSettings();

  if (auditorConstraints) {
    return null;
  }

  const pastRangeValue = isMaxMode ? null : pastRange;
  const futureRangeValue = isMaxMode ? null : futureRange;

  return (
    <FinanceDateSelector label={label}>
      <Stack p={'1rem 1rem 0'}>
        <Chip
          label='Max'
          onClick={handleMaxToggle}
          variant={isMaxMode ? 'filled' : 'outlined'}
          color={isMaxMode ? 'primary' : 'default'}
          size='medium'
          aria-pressed={isMaxMode}
          sx={{ minWidth: '8.5rem' }}
        />
      </Stack>
      <Stack display={'grid'} gridTemplateColumns={'1fr 1fr 1fr'} p={'0.75rem 1rem 1rem'} columnGap={'1rem'}>
        <div css={chipStack}>
          <PeriodPicker value={selectedFrequency} onChange={handleFrequencyChange} />
        </div>
        <div css={chipStack}>
          <PeriodRangePicker
            value={pastRangeValue}
            onChange={(val) => handleSelectRange(val, false)}
            frequency={selectedFrequency!}
          />
        </div>
        <div css={chipStack}>
          <PeriodRangePicker
            value={futureRangeValue}
            onChange={(val) => handleSelectRange(val, true)}
            frequency={selectedFrequency!}
            future
          />
        </div>
      </Stack>
      {PermissionService.get().hasPermission(PermissionKey.canViewMetrics) && (
        // mounts on first open and suspends until the metrics load; without fallbacks of its own a pending or failed
        // read hides the whole tab behind the tab's fallback, and ag-grid rebuilds the grid from scratch afterwards
        <GenericFallbacksWrapper suspenseFallback={<></>}>
          <MostRecentReportDate />
        </GenericFallbacksWrapper>
      )}
    </FinanceDateSelector>
  );
}

export function MostRecentReportDate() {
  const { company, mostRecentReportDate } = useMostRecentReportDate();
  const theme = useTheme();

  if (!mostRecentReportDate || !company) {
    return null;
  }

  return (
    <Stack>
      <Divider />
      <Stack
        p={'1rem'}
        display={'grid'}
        gridTemplateColumns={'auto 1fr'}
        columnGap={'0.5rem'}
        alignItems={'center'}
        justifyContent={'start'}
      >
        <AssessmentIcon htmlColor={theme.colors.secondary[40]} />
        <Typography variant='subtitle1'>{FMT.format('date', new Date(mostRecentReportDate))}</Typography>
        <span />
        <Typography
          variant={'caption'}
          color={'text.secondary'}
        >{`most recent report from ${company.name}`}</Typography>
      </Stack>
    </Stack>
  );
}
